import React, { useState } from 'react';
import { Badge, Btn, Card, Modal, Spinner } from '../UI';
import * as api from '../../api';
import {
  fmt, fmtDate,
  MPO_DELIVERY_COLOR, MPO_DELIVERY_LABEL,
  SUBMITTABLE_STATUSES, APPROVABLE_STATUSES, RETURNABLE_STATUSES,
  ISSUABLE_STATUSES, CANCELLABLE_STATUSES, CLOSEABLE_STATUSES, EDITABLE_STATUSES,
} from './mpoUtils';
import MPOStatusPill from './MPOStatusPill';
import MPOApprovalHistory from './MPOApprovalHistory';

const ic = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IcoBack  = () => <svg {...ic}><polyline points="15 18 9 12 15 6"/></svg>;
const IcoSend  = () => <svg {...ic}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
const IcoCheck = () => <svg {...ic}><polyline points="20 6 9 17 4 12"/></svg>;
const IcoEdit  = () => <svg {...ic}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IcoX     = () => <svg {...ic}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoReturn = () => <svg {...ic}><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>;
const IcoIssue  = () => <svg {...ic}><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>;

function SectionHeader({ title }) {
  return (
    <div style={{
      fontWeight: 800, fontSize: 11, color: '#1e3a8a',
      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <div style={{ width: 3, height: 14, borderRadius: 2, background: '#1e3a8a', flexShrink: 0 }} />
      {title}
    </div>
  );
}

function ViewField({ label, value, mono, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </span>
      {children || (
        <span style={{
          fontSize: 13, color: value ? '#1e293b' : '#cbd5e1',
          fontFamily: mono ? 'monospace' : undefined,
          fontWeight: mono ? 600 : undefined,
        }}>
          {value || '—'}
        </span>
      )}
    </div>
  );
}

function ActionModal({ title, onClose, onConfirm, busy }) {
  const [comment, setComment] = useState('');
  return (
    <Modal title={title} onClose={onClose} width={440}>
      <div style={{ padding: '4px 0 16px' }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
          COMMENT (optional)
        </label>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={3}
          placeholder="Add a comment..."
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, border: '1.5px solid #cbd5e1', borderRadius: 8, resize: 'vertical' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={() => onConfirm(comment)} disabled={busy}>
          {busy ? <Spinner size={14} /> : 'Confirm'}
        </Btn>
      </div>
    </Modal>
  );
}

// readOnly hides the approver/buyer actions; ownerActions keeps the requester's own Edit and Submit / Resubmit
// (My Orders opens POs read-only, yet a returned PO must still be correctable and resubmittable from there).
export default function MPODetailView({ po, onBack, onRefresh, onEdit, readOnly = false, ownerActions = false }) {
  const [actionModal, setActionModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const status    = po.status;
  const ownerMay  = !readOnly || ownerActions;
  const canEdit   = ownerMay && !!onEdit && EDITABLE_STATUSES.has(status);
  const canSubmit = ownerMay && SUBMITTABLE_STATUSES.has(status);
  // a returned PO goes back to the approval step that returned it
  const resubmitTo = status === 'returned' ? (po.returned_from_step_name || 'the step that returned it') : null;
  const canApprove = APPROVABLE_STATUSES.has(status);
  const canReturn  = RETURNABLE_STATUSES.has(status);
  const canIssue   = ISSUABLE_STATUSES.has(status);
  const canCancel  = CANCELLABLE_STATUSES.has(status);
  const canClose   = CLOSEABLE_STATUSES.has(status);

  const ACTION_FN = {
    submit:          (c) => api.submitMPO(po.po_id, { comment: c }),
    approve:         (c) => api.approveMPO(po.po_id, { comment: c }),
    return_to_draft: (c) => api.returnMPOToDraft(po.po_id, { comment: c }),
    issue:           (c) => api.issueMPO(po.po_id, { comment: c }),
    cancel:          (c) => api.cancelMPO(po.po_id, { comment: c }),
    close:           (c) => api.closeMPO(po.po_id, { comment: c }),
  };

  const runAction = async (comment) => {
    setBusy(true);
    setError(null);
    try {
      await ACTION_FN[actionModal.type](comment);
      setActionModal(null);
      onRefresh?.();
    } catch (e) {
      setError(e.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const thS = {
    padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 10,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#fff',
    whiteSpace: 'nowrap', background: '#1e3a8a',
    border: '1px solid rgba(255,255,255,0.18)',
  };
  const tdS = { padding: '7px 10px', border: '1px solid #e2e8f0', verticalAlign: 'middle', fontSize: 12, overflow: 'hidden' };
  const mono = { fontFamily: 'monospace', fontWeight: 600 };

  const downPaymentAmt = po.is_down_payment && po.down_payment && po.grand_total
    ? (parseFloat(po.down_payment) / 100) * parseFloat(po.grand_total)
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Back + title ─────────────────────────────────────────────────── */}
      <div>
        <button
          type="button"
          onClick={onBack}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#0b318bff', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 16, padding: '9px 18px' }}
        >
          <IcoBack /> Back to List
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                {po.po_number || po.serial_number}
                {po.po_number && <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>Serial {po.serial_number}</span>}
              </h2>
              <MPOStatusPill status={status} />
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
              {po.project_name}{po.project_code && ` (${po.project_code})`}
            </p>
          </div>

          {/* Action buttons */}
          {ownerMay && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {canEdit && (
              <button
                type="button"
                onClick={() => onEdit?.(po)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1.5px solid #1e3a8a', background: '#fff', color: '#1e3a8a', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <IcoEdit /> Edit
              </button>
            )}
            {canSubmit && (
              <button
                type="button"
                onClick={() => setActionModal({ type: 'submit', label: resubmitTo ? `Resubmit to ${resubmitTo}` : 'Submit for Approval' })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0b318bff', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                title={resubmitTo ? `Send the corrected PO back to ${resubmitTo}` : undefined}
              >
                <IcoSend /> {resubmitTo ? `Resubmit to ${resubmitTo}` : 'Submit'}
              </button>
            )}
            {!readOnly && canApprove && (
              <button
                type="button"
                onClick={() => setActionModal({ type: 'approve', label: 'Approve PO' })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <IcoCheck /> Approve
              </button>
            )}
            {!readOnly && canReturn && (
              <button
                type="button"
                onClick={() => setActionModal({ type: 'return_to_draft', label: 'Return to Draft' })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1.5px solid #94a3b8', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <IcoReturn /> Return
              </button>
            )}
            {!readOnly && canIssue && (
              <button
                type="button"
                onClick={() => setActionModal({ type: 'issue', label: 'Issue PO' })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0891b2', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <IcoIssue /> Issue
              </button>
            )}
            {!readOnly && canClose && (
              <button
                type="button"
                onClick={() => setActionModal({ type: 'close', label: 'Close PO' })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1.5px solid #94a3b8', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Close
              </button>
            )}
            {!readOnly && canCancel && (
              <button
                type="button"
                onClick={() => setActionModal({ type: 'cancel', label: 'Cancel PO' })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <IcoX /> Cancel
              </button>
            )}
          </div>}
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 12.5 }}>
          {error}
        </div>
      )}

      {status === 'returned' && (
        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderLeft: '4px solid #f97316', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#9a3412' }}>
          <strong>Returned{po.returned_from_step_name ? ` by ${po.returned_from_step_name}` : ''}</strong>
          {po.return_reason ? <> — "{po.return_reason}"</> : null}
          <div style={{ marginTop: 4, color: '#c2410c' }}>
            Correct the purchase order, then resubmit it — it goes straight back to {resubmitTo}.
          </div>
        </div>
      )}

      {/* ── Section 1 — PO Header ─────────────────────────────────────────── */}
      <Card style={{ padding: '18px 22px' }}>
        <SectionHeader title="Section 1 — PO Header" />

        {/* Row 1 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
          <ViewField label="REQUESTED BY" value={po.requested_by_name || po.created_by_name} />
          <ViewField label="PROJECT" value={po.project_name ? `${po.project_code ? po.project_code + ' — ' : ''}${po.project_name}` : po.project_code} />
          <ViewField label="SUPPLIER" value={po.supplier_name} />
          <ViewField label="CURRENCY" value={po.currency} />
          <ViewField label="PO DATE" value={fmtDate(po.po_date)} />
        </div>

        {/* Row 2 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginTop: 16 }}>
          <ViewField label="EXPECTED DELIVERY" value={fmtDate(po.expected_delivery_date)} />
          <ViewField label="DELIVERY DATE" value={fmtDate(po.delivery_date)} />
          <ViewField label="PAYMENT TERMS" value={po.payment_terms} />
          <ViewField label="CATEGORY" value={po.category} />
          <ViewField label="DELIVERY TERMS" value={po.delivery_terms} />
        </div>

        {/* Row 3 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 16, marginTop: 16 }}>
          <ViewField label={`CHARGES (${po.currency || 'NGN'})`} value={po.charges != null ? fmt(po.charges) : '—'} mono />
          <ViewField label="DELIVERY ADDRESS" value={po.delivery_address} />
        </div>

        {(po.notes || po.internal_remarks) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            {po.notes && <ViewField label="NOTES" value={po.notes} />}
            {po.internal_remarks && <ViewField label="INTERNAL REMARKS" value={po.internal_remarks} />}
          </div>
        )}

        {/* Down Payment */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: po.is_down_payment ? '#1e3a8a' : '#94a3b8' }}>
            Down Payment
          </span>
          {po.is_down_payment && po.down_payment != null ? (
            <>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>{po.down_payment}%</span>
              {downPaymentAmt != null && (
                <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                  = {po.currency || 'NGN'} {downPaymentAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </>
          ) : (
            <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>= NO ADVANCE</span>
          )}
        </div>

        {/* Submission info */}
        {(po.submitted_by_name || po.submitted_date) && (
          <div style={{ display: 'flex', gap: 24, marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
            {po.submitted_by_name && <ViewField label="Submitted By" value={po.submitted_by_name} />}
            {po.submitted_date    && <ViewField label="Submitted Date" value={fmtDate(po.submitted_date)} />}
          </div>
        )}
      </Card>

      {/* ── Section 2 — Line Items ────────────────────────────────────────── */}
      <Card style={{ padding: '18px 22px' }}>
        <SectionHeader title={`Section 2 — Line Items (${(po.items || []).length})`} />

        <div style={{ overflowX: 'auto', border: '1.5px solid #bfdbfe', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ ...thS, minWidth: 42,  textAlign: 'center' }}>#</th>
                <th style={{ ...thS, minWidth: 220 }}>Item Code</th>
                <th style={{ ...thS, minWidth: 300 }}>Description</th>
                <th style={{ ...thS, minWidth: 280 }}>New Item Description</th>
                <th style={{ ...thS, minWidth: 240 }}>Specification</th>
                <th style={{ ...thS, minWidth: 200 }}>Warehouse</th>
                <th style={{ ...thS, minWidth: 220 }}>Request Ref</th>
                <th style={{ ...thS, minWidth: 180 }}>BOQ Code</th>
                <th style={{ ...thS, minWidth: 220 }}>Material Code</th>
                <th style={{ ...thS, minWidth: 180, textAlign: 'right' }}>Rem. Rqst Qty</th>
                <th style={{ ...thS, minWidth: 140 }}>Unit</th>
                <th style={{ ...thS, minWidth: 160, textAlign: 'right' }}>Qty Ordered</th>
                <th style={{ ...thS, minWidth: 160, textAlign: 'right' }}>Qty Received</th>
                <th style={{ ...thS, minWidth: 180, textAlign: 'right' }}>Last Pur.Pr</th>
                <th style={{ ...thS, minWidth: 200, textAlign: 'right' }}>Price</th>
                <th style={{ ...thS, minWidth: 120, textAlign: 'center' }}>Disc %</th>
                <th style={{ ...thS, minWidth: 120, textAlign: 'center' }}>VAT %</th>
                <th style={{ ...thS, minWidth: 200, textAlign: 'right' }}>Total</th>
                <th style={{ ...thS, minWidth: 200 }}>Remark</th>
                <th style={{ ...thS, minWidth: 130, textAlign: 'center' }}>Delivery</th>
              </tr>
            </thead>
            <tbody>
              {(po.items || []).length === 0 && (
                <tr>
                  <td colSpan={20} style={{ ...tdS, padding: '28px 10px', textAlign: 'center', color: '#94a3b8' }}>
                    No items.
                  </td>
                </tr>
              )}
              {(po.items || []).map((it, idx) => (
                <tr key={it.item_id || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...tdS, color: '#94a3b8', textAlign: 'center', fontWeight: 700 }}>{it.line_number || idx + 1}</td>
                  <td style={{ ...tdS, ...mono, fontSize: 11.5 }}>{it.material_code || '—'}</td>
                  <td style={tdS}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{it.material_description || '—'}</div>
                  </td>
                  <td style={{ ...tdS, color: '#64748b' }}>{it.new_item_description || '—'}</td>
                  <td style={{ ...tdS, color: '#64748b' }}>{it.specification || '—'}</td>
                  <td style={{ ...tdS, color: '#64748b' }}>{it.warehouse || '—'}</td>
                  <td style={{ ...tdS, color: '#64748b' }}>{it.request_ref || '—'}</td>
                  <td style={{ ...tdS, color: '#64748b' }}>{it.boq_item_code || '—'}</td>
                  <td style={{ ...tdS, ...mono, fontSize: 11.5 }}>{it.material_code || '—'}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#64748b' }}>
                    {it.rem_rqst_qty != null ? Number(it.rem_rqst_qty).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '—'}
                  </td>
                  <td style={{ ...tdS, color: '#64748b' }}>{it.unit_of_measure || '—'}</td>
                  <td style={{ ...tdS, ...mono, textAlign: 'right' }}>
                    {Number(it.quantity_ordered).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ ...tdS, ...mono, textAlign: 'right', color: '#16a34a' }}>
                    {Number(it.quantity_received || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ ...tdS, ...mono, textAlign: 'right', color: '#64748b' }}>
                    {it.last_pur_pr != null ? fmt(it.last_pur_pr) : '—'}
                  </td>
                  <td style={{ ...tdS, ...mono, textAlign: 'right' }}>{fmt(it.unit_rate)}</td>
                  <td style={{ ...tdS, textAlign: 'center', color: '#64748b' }}>
                    {it.disc != null ? `${it.disc}%` : '—'}
                  </td>
                  <td style={{ ...tdS, textAlign: 'center', color: '#64748b' }}>
                    {it.vat_percentage != null ? `${it.vat_percentage}%` : '—'}
                  </td>
                  <td style={{ ...tdS, ...mono, textAlign: 'right', color: '#1e40af', fontWeight: 800 }}>{fmt(it.amount)}</td>
                  <td style={{ ...tdS, color: '#64748b', fontSize: 11.5 }}>{it.remarks || '—'}</td>
                  <td style={{ ...tdS, textAlign: 'center' }}>
                    <Badge color={MPO_DELIVERY_COLOR[it.delivery_status] || 'gray'}>
                      {MPO_DELIVERY_LABEL[it.delivery_status] || it.delivery_status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <div style={{ background: '#f0f7ff', border: '1.5px solid #bfdbfe', borderRadius: 10, padding: '14px 22px', minWidth: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 7, color: '#475569' }}>
              <span style={{ fontWeight: 600 }}>Subtotal</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmt(po.subtotal_amount)}</span>
            </div>
            {po.charges != null && parseFloat(po.charges) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 7, color: '#475569' }}>
                <span style={{ fontWeight: 600 }}>Charges</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmt(po.charges)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 12, color: '#475569' }}>
              <span style={{ fontWeight: 600 }}>VAT</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmt(po.vat_amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, borderTop: '1.5px solid #bfdbfe', paddingTop: 10, color: '#1e40af' }}>
              <span>Grand Total</span>
              <span style={{ fontFamily: 'monospace' }}>{fmt(po.grand_total)}</span>
            </div>
            {downPaymentAmt != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 8, color: '#0891b2', fontWeight: 600 }}>
                <span>Down Payment ({po.down_payment}%)</span>
                <span style={{ fontFamily: 'monospace' }}>{fmt(downPaymentAmt)}</span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ── Approval Progress & History ───────────────────────────────────── */}
      <MPOApprovalHistory po={po} />

      {/* ── Action modal ─────────────────────────────────────────────────── */}
      {actionModal && (
        <ActionModal
          title={actionModal.label}
          onClose={() => { setActionModal(null); setError(null); }}
          onConfirm={runAction}
          busy={busy}
        />
      )}
    </div>
  );
}
